import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Plus } from 'lucide-react'
import type { Payment } from '@shared/types/payment.types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import { Checkbox } from '@shared/components/ui/checkbox'
import { Input } from '@shared/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/radio-group'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover'
import { DialogResultTransition, type DialogStatus } from '@shared/components/ui/DialogResultTransition'
import { OperationCommentsField } from '../../shared/components/forms/OperationCommentsField'
import { paymentOperationsService } from '@shared/services/payment-operations.service'
import { emailSchema } from '@shared/utils/validation'
import type { SendDocumentType } from '../../shared/types/operation-types'

export interface SendInvoiceReceiptDialogProps {
  open: boolean
  payment: Payment
  onClose: () => void
  onSuccess?: () => void
}

type EmailOption = 'customer' | 'custom'

const DOCUMENT_OPTIONS: { value: SendDocumentType; label: string }[] = [
  { value: 'receipt', label: 'Receipt' },
  { value: 'invoice', label: 'Invoice' },
]

const AUTO_CLOSE_MS = 1500

export function SendInvoiceReceiptDialog({
  open,
  payment,
  onClose,
  onSuccess,
}: SendInvoiceReceiptDialogProps) {
  const customerEmail = payment.customer?.email ?? ''

  const [documents, setDocuments] = useState<SendDocumentType[]>([])
  const [emailOption, setEmailOption] = useState<EmailOption>(customerEmail ? 'customer' : 'custom')
  const [customEmail, setCustomEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [documentsOpen, setDocumentsOpen] = useState(false)
  const [status, setStatus] = useState<DialogStatus>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (open) {
      setDocuments([])
      setEmailOption(customerEmail ? 'customer' : 'custom')
      setCustomEmail('')
      setNotes('')
      setIsLoading(false)
      setDocumentsOpen(false)
      setStatus('idle')
    }
    return () => clearTimeout(timerRef.current)
  }, [open, customerEmail])

  const selectedEmail = emailOption === 'customer' ? customerEmail : customEmail
  const isEmailValid = emailSchema.safeParse(selectedEmail).success
  const isValid = documents.length > 0 && isEmailValid

  function handleDocumentToggle(doc: SendDocumentType) {
    setDocuments((prev) =>
      prev.includes(doc) ? prev.filter((d) => d !== doc) : [...prev, doc]
    )
  }

  const selectedLabels = DOCUMENT_OPTIONS
    .filter(({ value }) => documents.includes(value))
    .map(({ label }) => label)

  async function handleSend() {
    if (!isValid) return
    setIsLoading(true)
    try {
      await paymentOperationsService.sendReceipt({
        paymentId: payment.id,
        documents,
        email: selectedEmail,
        notes,
      })
      setStatus('success')
      timerRef.current = setTimeout(() => {
        onSuccess?.()
        onClose()
      }, AUTO_CLOSE_MS)
    } catch {
      setStatus('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && status !== 'success') onClose() }}>
      <DialogContent className="max-w-md">
        <DialogResultTransition
          status={status}
          successMessage="Sent successfully"
          errorMessage="Failed to send"
          errorDescription="Something went wrong. Please try again."
          onRetry={() => setStatus('idle')}
          onClose={onClose}
        >
          <DialogHeader>
            <DialogTitle>Send invoice/receipt</DialogTitle>
            <DialogDescription>
              Choose what to send and the email address to deliver it to.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Document type selection — dropdown with multiselect */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">What would you like to send?</p>
              <Popover open={documentsOpen} onOpenChange={setDocumentsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between rounded-xl font-normal"
                  >
                    <span className="truncate text-left">
                      {selectedLabels.length > 0
                        ? selectedLabels.join(', ')
                        : 'Select documents\u2026'}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
                  {DOCUMENT_OPTIONS.map(({ value, label }) => {
                    const checked = documents.includes(value)
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleDocumentToggle(value)}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-muted transition-colors"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => handleDocumentToggle(value)}
                          className="shrink-0 pointer-events-none"
                          aria-label={`Send ${label}`}
                        />
                        {label}
                      </button>
                    )
                  })}
                </PopoverContent>
              </Popover>
            </div>

            {/* Email selection — no borders */}
            <div className="space-y-2.5">
              <p className="text-sm font-medium text-muted-foreground">Send to</p>
              <RadioGroup
                value={emailOption}
                onValueChange={(value) => setEmailOption(value as EmailOption)}
              >
                {customerEmail && (
                  <label
                    htmlFor="email-customer"
                    className="flex items-center gap-3 px-1 py-2 cursor-pointer hover:bg-muted/50 rounded-lg transition-colors"
                  >
                    <RadioGroupItem value="customer" id="email-customer" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-tight">{payment.customer?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{customerEmail}</p>
                    </div>
                  </label>
                )}
                <label
                  htmlFor="email-custom"
                  className="flex items-center gap-3 px-1 py-2 cursor-pointer hover:bg-muted/50 rounded-lg transition-colors"
                >
                  <RadioGroupItem value="custom" id="email-custom" />
                  <span className="flex items-center gap-1.5 text-sm">
                    <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                    Other email address
                  </span>
                </label>
              </RadioGroup>
              {emailOption === 'custom' && (
                <Input
                  type="email"
                  placeholder="name@example.com"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  autoFocus
                />
              )}
            </div>

            {/* Note to customer (optional) */}
            <OperationCommentsField
              value={notes}
              onChange={setNotes}
              label="Note to customer (optional)"
              placeholder="Add a note that will be included in the email..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose} className="font-medium">
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={isLoading || !isValid}
              className="rounded-full px-6"
            >
              {isLoading ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogResultTransition>
      </DialogContent>
    </Dialog>
  )
}
